/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';

import { compose } from 'recompose';

import Retracked from 'js/app/retracked';
import Imgix from 'js/components/Imgix';

import type { Theme } from '@coursera/cds-core';
import { Grid, Typography, Typography2 } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import PersonalizedEmbeddedMsg from 'bundles/degree-description/components/modules/personalization/PersonalizedEmbeddedMsg';
import { getCtaActionXddp, getMessagesToDisplay } from 'bundles/degree-description/utils/personalizedMessageCriteria';
import type { Maybe } from 'bundles/xddp/__generated__/baseContentfulTypes';
import AboutCard from 'bundles/xddp/components/about/AboutCard';
import AboutPageSchemaMarkup from 'bundles/xddp/components/seo/AboutPageSchemaMarkup';
import Container from 'bundles/xddp/components/shared/Container';
import InfoSidebar from 'bundles/xddp/components/shared/InfoSidebar';
import { useScrollToStickyBar } from 'bundles/xddp/hooks/useScrollToStickyBar';
import type { DegreeSubPageProps, FacultyInstructor } from 'bundles/xddp/types';

import _t from 'i18n!nls/xddp';

import 'css!bundles/xddp/components/__styles__/AboutPage';

const styles: Record<string, Interpolation<Theme>> = {
  instructor: css`
    font-size: 18px;
    line-height: 30px;
    font-weight: 400;
  `,
};

export const InstructorProfile: React.FC<{ instructor: FacultyInstructor }> = ({ instructor }) => (
  <div data-testid="rc-InstructorProfile" className="rc-InstructorProfile">
    <Imgix
      className="instructor-headshot"
      src={instructor?.profilePicture?.url ?? ''}
      alt={instructor.name ?? 'Instructor'}
    />
    <div className="instructor-info">
      <Typography2 component="h3" variant="subtitleMedium" css={styles.instructor}>
        {instructor.name}
      </Typography2>
      <Typography2 component="p" variant="bodyPrimary">
        <strong>{instructor.position}</strong>
      </Typography2>
      <Markdown
        data-testid="instructor-introduction"
        className="instructor-introduction"
        source={instructor.introduction}
      />
    </div>
  </div>
);

const AboutPage: React.FC<DegreeSubPageProps> = ({
  pageContent,
  userAgent,
  personalizedDdpData,
  handlePersonalizedRequestInfoClick,
  isStickyBarPresent,
}) => {
  const {
    aboutUniversity,
    aboutDepartment,
    historyOfOnlineLearning,
    atAGlance,
    facultyCollection,
    slug,
    programName,
    partnerInformation,
    expressionOfInterestContainer,
  } = pageContent;

  useScrollToStickyBar(isStickyBarPresent);

  const messagesToDisplay = getMessagesToDisplay(
    personalizedDdpData,
    slug,
    programName,
    partnerInformation?.partnerName
  );

  const embeddedAction = getCtaActionXddp(
    messagesToDisplay?.embeddedMessageToDisplay?.ctaActionType,
    expressionOfInterestContainer?.applyButtonUrl ?? expressionOfInterestContainer?.hostedApplicationUrl,
    handlePersonalizedRequestInfoClick
  );

  const faculties: Maybe<FacultyInstructor>[] = facultyCollection?.items ?? [];

  return (
    <div className="rc-AboutPage">
      <AboutPageSchemaMarkup pageContent={pageContent} />
      <Container>
        <Grid container>
          <Grid item md={8} sm={12}>
            <AboutCard {...aboutUniversity} />
            {messagesToDisplay.embeddedMessageToDisplay && (
              <PersonalizedEmbeddedMsg
                personalizedMessage={messagesToDisplay.embeddedMessageToDisplay}
                ctaAction={embeddedAction}
                onXddp
              />
            )}
            <AboutCard {...aboutDepartment} />
            <AboutCard {...historyOfOnlineLearning} />

            {faculties.length > 0 && (
              <div className="rc-Faculty styled">
                <Typography variant="h2">{_t('Featured Faculty')}</Typography>
                {faculties.map(
                  (instructor) =>
                    instructor && <InstructorProfile key={instructor?.profilePicture?.url} instructor={instructor} />
                )}
              </div>
            )}
          </Grid>
          <InfoSidebar userAgent={userAgent} atAGlance={atAGlance} />
        </Grid>
      </Container>
    </div>
  );
};

export default compose<DegreeSubPageProps, DegreeSubPageProps>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'xddp',
      page: 'about_page',
    },
  }))
)(AboutPage);
